 

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Modal,
  Text,
  BlockStack,
  Button,
  Link,
  DropZone,
  InlineStack,
  Banner,
} from "@medentic/polaris-react";
import { useMutation } from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { ROOTS } from "@app/config/constants";
import { URLLinkBuilder } from "@shared/lib";

interface ImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess: (importId: string) => void;
}

export function ImportModal({
  open,
  onClose,
  onImportSuccess,
}: ImportModalProps) {
  const { t, i18n } = useTranslation();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { GET, POST } = useApiRequest();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  // Download sample file query
  const handleDownloadSample = async () => {
    try {
      const response = await GET<{ url: string }>(
        `/api/healthcare-companies/${clinicId}/patients/export-import-csv`
      );

      // Create a download link for the CSV using the pre-signed URL
      const link = document.createElement("a");
      link.href = response.url;
      link.setAttribute("download", "patient-import-template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError(t("patients.import.sampleDownloadError"));
    }
  };

  // Upload file mutation
  const uploadMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return POST<{ id: string }>("/api/imports", formData);
    },
    onSuccess: (response) => {
      onImportSuccess(response.id);
      // Navigate to the import configuration page
      navigate(`${ROOTS.CLINIC}/${clinicId}/patients/import/${response.id}`);
    },
  });

  const handleDrop = useCallback(
    (_dropFiles: File[], acceptedFiles: File[]) => {
      setError("");
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    },
    []
  );

  const handleUpload = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("source", "web_app");
    formData.append("hccId", clinicId || "");

    uploadMutation.mutate(formData);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("patients.import.title")}
      primaryAction={{
        content: t("patients.import.upload"),
        onAction: handleUpload,
        disabled: !file || uploadMutation.isPending,
        loading: uploadMutation.isPending,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: onClose,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p">
            {t("patients.import.description")}{" "}
            <Link
              url={URLLinkBuilder.supportHelpCenter("/collections/11565489", i18n.language)}
            >
              {t("patients.import.readyForImport")}
            </Link>
          </Text>

          {error && (
            <Banner tone="critical">
              <Text as="p">{error}</Text>
            </Banner>
          )}

          <DropZone
            accept=".csv,.xlsx,.xls"
            onDrop={handleDrop}
            allowMultiple={false}
          >
            <DropZone.FileUpload
              actionTitle={t("patients.import.uploadFiles")}
            />
          </DropZone>

          {file && (
            <Text as="p" variant="bodyMd">
              {t("patients.import.selectedFile")}: {file.name}
            </Text>
          )}

          <InlineStack>
            <Button onClick={handleDownloadSample} variant="plain">
              {t("patients.import.downloadSample")}
            </Button>
          </InlineStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
